/*
 * @author Andrey Patyuchenko
 * @email  apatyuchenko@atlassian.com
 * @email  andrey.patyuchenko@gmail.com
 * @date   10/11/2022
 */

import React from "react";
import { Stack, Typography } from "@mui/material";
import { LoadingButton, Timeline } from "@mui/lab";
import TimelineOppositeContent from "@mui/lab/TimelineOppositeContent";
import ConfigDelivery from "./ConfigDelivery";
import TimelineSeparator from "@mui/lab/TimelineSeparator";
import TimelineDot from "@mui/lab/TimelineDot";
import SendIcon from "@mui/icons-material/Send";
import ScheduleIcon from '@mui/icons-material/Schedule';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import SourceIcon from '@mui/icons-material/Source';
import SettingsIcon from '@mui/icons-material/Settings';
import TimelineConnector from "@mui/lab/TimelineConnector";
import TimelineContent from "@mui/lab/TimelineContent";
import TimelineItem from "@mui/lab/TimelineItem";
import { SummaryNotificationConfig } from "../../interfaces/Global";
import { useForm } from "@mantine/form";
import ConfigSchedule from "./ConfigSchedule";
import ConfigContent from "./ConfigContent";
import ConfigEmployees from "./ConfigEmployees";
import ConfigSettings from "./ConfigSettings";
import ErrorsDisplay from "../../components/ErrorsDisplay";
import WarningIcon from "@mui/icons-material/Warning";
import CheckIcon from "@mui/icons-material/Check";
import { useMutation } from "urql";
import {
	CreateSummaryNotificationMutation,
	CreateSummaryNotificationResponse,
	CreateSummaryNotificationVars, SummaryNotificationInput,
	UpdateSummaryNotificationMutation, UpdateSummaryNotificationResponse, UpdateSummaryNotificationVars
} from "../../graphql/mutations/summary-notifications";
import { useNavigate } from "react-router-dom";

export interface SummaryFormProps {
	id?: number;
	disabled?: boolean;
	config: SummaryNotificationConfig;
}

export default function SummaryForm({ id, disabled, config }: SummaryFormProps) {

	const navigate = useNavigate();

	const form = useForm<SummaryNotificationConfig>({
		initialValues: config,
		validate: {
			daysOfWeek: (value) => value.length === 0 ? "At least one day of week must be specified" : null,
			times: (value) => value.length === 0 ? "Time of notification must be specified" : null,
			calendarId: (value, values) => {
				if (values.employeesSource === "calendar" && !value) {
					return "Linked calendar must be specified when employees are set to be sourced from it.";
				}
				return null;
			},
			// @ts-ignore -have to keep that because Typescript compiler cannot infer those for some reason
			capacityFormat: (value, values) => {
				if (value === "off" && values.employeeNameFormat === "off") return "Either Employee Name Format or Group Aggregate Number or both must be specified";
				return null;
			},
			// @ts-ignore
			employeeNameFormat: (value, values) => {
				if (value === "off" && values.capacityFormat === "off") return "Either Employee Name Format or Group Aggregate Number or both must be specified";
				return null;
			}
		}
	});

	const [createResult, createSummaryNotification] = useMutation<CreateSummaryNotificationResponse, CreateSummaryNotificationVars>(CreateSummaryNotificationMutation);
	const [updateResult, updateSummaryNotification] = useMutation<UpdateSummaryNotificationResponse, UpdateSummaryNotificationVars>(UpdateSummaryNotificationMutation);

	return (
		<form onSubmit={form.onSubmit((values) => {
			const data: SummaryNotificationInput = { config: values };
			const promise = !id ? createSummaryNotification({ data: { ...data, enabled: true } }) : updateSummaryNotification({ id, data });
			promise.then(response => {
				if (response.data) {
					navigate("/notifications/summaries");
				}
			});
		})}>
			<Timeline position={"left"}>
				<TimelineItem>
					<TimelineOppositeContent sx={{ flexGrow: 5 }}>
						<Stack sx={{ width: "60%" }}>
							<ConfigDelivery
								form={form}
							/>
						</Stack>
					</TimelineOppositeContent>
					<TimelineSeparator>
						<TimelineDot>
							<SendIcon />
						</TimelineDot>
						<TimelineConnector/>
					</TimelineSeparator>
					<TimelineContent sx={{ py: "12px", px: 2 }}>
						<Typography variant="h6" component="span">
							Delivery
						</Typography>
					</TimelineContent>
				</TimelineItem>
				<TimelineItem>
					<TimelineOppositeContent sx={{ flexGrow: 5 }}>
						<Stack sx={{ width: "60%" }}>
							<ConfigSchedule
								form={form}
							/>
						</Stack>
					</TimelineOppositeContent>
					<TimelineSeparator>
						<TimelineDot>
							<ScheduleIcon />
						</TimelineDot>
						<TimelineConnector/>
					</TimelineSeparator>
					<TimelineContent sx={{ py: "12px", px: 2 }}>
						<Typography variant="h6" component="span">
							Schedule
						</Typography>
					</TimelineContent>
				</TimelineItem>
				<TimelineItem>
					<TimelineOppositeContent sx={{ flexGrow: 5 }}>
						<Stack sx={{ width: "100%" }}>
							<ConfigEmployees
								form={form}
							/>
						</Stack>
					</TimelineOppositeContent>
					<TimelineSeparator>
						<TimelineDot>
							<PeopleAltIcon />
						</TimelineDot>
						<TimelineConnector/>
					</TimelineSeparator>
					<TimelineContent sx={{ py: "12px", px: 2 }}>
						<Typography variant="h6" component="span">
							Employees
						</Typography>
					</TimelineContent>
				</TimelineItem>
				<TimelineItem>
					<TimelineOppositeContent sx={{ flexGrow: 5 }}>
						<Stack sx={{ width: "60%" }}>
							<ConfigContent
								form={form}
							/>
						</Stack>
					</TimelineOppositeContent>
					<TimelineSeparator>
						<TimelineDot>
							<SourceIcon />
						</TimelineDot>
						<TimelineConnector/>
					</TimelineSeparator>
					<TimelineContent sx={{ py: "12px", px: 2 }}>
						<Typography variant="h6" component="span">
							Content
						</Typography>
					</TimelineContent>
				</TimelineItem>
				<TimelineItem>
					<TimelineOppositeContent sx={{ flexGrow: 5 }}>
						<Stack sx={{ width: "60%" }}>
							<ConfigSettings
								form={form}
							/>
						</Stack>
					</TimelineOppositeContent>
					<TimelineSeparator>
						<TimelineDot>
							<SettingsIcon />
						</TimelineDot>
						<TimelineConnector/>
					</TimelineSeparator>
					<TimelineContent sx={{ py: "12px", px: 2 }}>
						<Typography variant="h6" component="span">
							Settings
						</Typography>
					</TimelineContent>
				</TimelineItem>
				{Object.keys(form.errors).length > 0 &&
					<TimelineItem>
						<TimelineOppositeContent sx={{ flexGrow: 5 }}>
							<Stack sx={{ width: "100%" }}>
								<ErrorsDisplay
									errors={form.errors}
								/>
							</Stack>
						</TimelineOppositeContent>
						<TimelineSeparator>
							<TimelineDot>
								<WarningIcon />
							</TimelineDot>
							<TimelineConnector />
						</TimelineSeparator>
						<TimelineContent sx={{ py: '12px', px: 2 }}>
							<Typography variant="h6" component="span">
								Errors
							</Typography>
						</TimelineContent>
					</TimelineItem>}
				<TimelineItem>
					<TimelineOppositeContent sx={{ flexGrow: 5 }}>
						<LoadingButton
							variant={"contained"}
							size={"large"}
							sx={{ width: "300px" }}
							loadingPosition={"start"}
							startIcon={<CheckIcon />}
							type={"submit"}
							disabled={disabled}
							loading={createResult.fetching || updateResult.fetching}
						>
							{id ? "Update" : "Create"} Notification
						</LoadingButton>
					</TimelineOppositeContent>
					<TimelineSeparator>
						<TimelineDot>
							<CheckIcon />
						</TimelineDot>
					</TimelineSeparator>
					<TimelineContent sx={{ py: '12px', px: 2 }} />
				</TimelineItem>
			</Timeline>
		</form>
	);

}